import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, map, Observable } from "rxjs";
import { CollectionOfProduct, DefaultProduct } from "./generated";
import { ConfigService } from "../config/config.service";
import { ProductSeriesClusteredByFamily } from "../models/product-series-clustered-by-family";

@Injectable({
  providedIn: "root",
})
export class PiaProductsService {
  configService = inject(ConfigService);
  http = inject(HttpClient);

  getProductDataByCountryAndLanguageAndYear(
    {
      countryID,
      languageID,
      year,
    }: {
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Year(ID=${encodeURIComponent(year.toString())},Country='${encodeURIComponent(countryID)}',Language='${encodeURIComponent(languageID)}')/Products`;
    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfProduct }>(url, {
      headers,
      context,
    });
  }

  private getProductFamiliesFromResponse(
    response: Observable<{ d?: CollectionOfProduct }>,
  ) {
    return response.pipe(
      map((productFamilies) => {
        const productFamiliesResult = productFamilies?.d?.results
          ?.filter((productFamily) => {
            return productFamily?.ID?.startsWith("pr_fam");
          })
          .sort(
            (productFamilyA: DefaultProduct, productFamilyB: DefaultProduct) =>
              parseInt(String(productFamilyA.Sort), 10) -
              parseInt(String(productFamilyB.Sort), 10),
          );

        if (productFamiliesResult?.length) {
          return productFamiliesResult;
        }

        return [];
      }),
    );
  }

  getProductSeriesByYearAndCountryAndLanguageClusteredByProductFamily(
    {
      countryID,
      languageID,
      year,
    }: {
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ): Observable<ProductSeriesClusteredByFamily | null> {
    const response = this.getProductDataByCountryAndLanguageAndYear(
      {
        countryID,
        languageID,
        year,
      },
      context,
    );
    const productFamiliesResponse =
      this.getProductFamiliesFromResponse(response);

    return combineLatest([response, productFamiliesResponse]).pipe(
      map(
        ([productData, productFamilies]: [
          { d?: CollectionOfProduct } | undefined,
          DefaultProduct[],
        ]) => {
          const productFamilyIds = productFamilies
            .map((productFamily: DefaultProduct) => productFamily.ID)
            .filter(Boolean);
          const productDataResults = productData?.d?.results;
          const productSeriesResult = productDataResults
            ?.filter(
              (productDataset) =>
                productDataset.ParentId &&
                productFamilyIds.includes(productDataset.ParentId),
            )
            .reduce((productFamilyClusters, currentProductData) => {
              const clusterKey =
                currentProductData.ParentId as keyof typeof productFamilyClusters;
              if (!productFamilyClusters[clusterKey]) {
                productFamilyClusters[clusterKey] = [] as DefaultProduct[];
              }

              const displayRange = productFamilies.find(
                (productFamily) =>
                  currentProductData.ParentId === productFamily.ID,
              )?.DisplayRange;

              if (!displayRange) {
                const productSeriesRange = productDataResults.filter(
                  (productDataResult) =>
                    productDataResult.ParentId === currentProductData.ID,
                );
                productFamilyClusters[clusterKey] = [
                  ...productFamilyClusters[clusterKey],
                  ...productSeriesRange,
                ].sort((a, b) => {
                  return parseInt(String(a), 10) - parseInt(String(b), 10);
                });
              } else {
                productFamilyClusters[clusterKey] = [
                  ...productFamilyClusters[clusterKey],
                  currentProductData,
                ].sort((a, b) => {
                  return parseInt(String(a), 10) - parseInt(String(b), 10);
                });
              }

              return productFamilyClusters;
            }, {} as ProductSeriesClusteredByFamily);

          return productSeriesResult ?? null;
        },
      ),
    );
  }

  getProductFamiliesByYearAndCountryAndLanguage(
    {
      countryID,
      languageID,
      year,
    }: {
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ) {
    const response = this.getProductDataByCountryAndLanguageAndYear(
      {
        countryID,
        languageID,
        year,
      },
      context,
    );

    return this.getProductFamiliesFromResponse(response);
  }

  getProductDetailsByProductID(
    {
      productID,
      year,
      languageID,
      countryID,
    }: {
      productID: string;
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Product(ID='${encodeURIComponent(productID)}',Year=${encodeURIComponent(year)},Language='${encodeURIComponent(languageID)}',Country='${encodeURIComponent(countryID)}')?$expand=Arguments/Argument,Exclude,TechData/Category,Comparisons/Competitor,Documents/Document/Categories`;
    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: DefaultProduct }>(url, { headers, context });
  }

  getProductDescendantsByProductID(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    return this.getProductDataByCountryAndLanguageAndYear(
      {
        countryID,
        languageID,
        year,
      },
      context,
    ).pipe(
      map((response) => {
        const allProducts = response?.d?.results;
        if (!allProducts?.length) {
          return [];
        }

        // Find the product with the given productID (could be series or range)
        const currentProduct = allProducts.find((p) => p.ID === productID);
        if (!currentProduct) {
          return [];
        }

        // Climb up using ParentId until we find a product family (ID starts with 'pr_fam')
        let productFamily: DefaultProduct | undefined;
        let currentNode: DefaultProduct | undefined = currentProduct;

        while (currentNode && !currentNode.ID?.startsWith("pr_fam")) {
          if (!currentNode.ParentId) {
            break;
          }
          currentNode = allProducts.find((p) => p.ID === currentNode?.ParentId);
          if (currentNode?.ID?.startsWith("pr_fam")) {
            productFamily = currentNode;
            break;
          }
        }

        // If we couldn't find a product family, the hierarchy is invalid
        if (!productFamily) {
          return [];
        }

        // Check displayRange property
        if (!productFamily.DisplayRange) {
          // If displayRange is false, just fetch products with the original productID as parentId
          const directProducts = allProducts.filter(
            (product) => product?.ParentId === productID,
          );
          // Get product IDs to find their subtypes (one level deeper)
          const directProductIds = directProducts
            .map((product) => product.ID)
            .filter((id): id is string => !!id);
          // Get all product subtypes that have these products as parentId

          const directProductSubtypes = allProducts
            .filter(
              (subtype) =>
                subtype?.ParentId &&
                directProductIds.includes(subtype.ParentId),
            )
            .sort((a, b) => Number(a.Sort) - Number(b.Sort));

          return directProductSubtypes?.length ? directProductSubtypes : [];
        }

        // If displayRange is true, we need to find the range
        // The range is between the product family and the series
        let rangeProduct: DefaultProduct | undefined;
        currentNode = currentProduct;

        // Climb up to find the range (child of product family)
        while (currentNode && currentNode.ID !== productFamily.ID) {
          if (currentNode.ParentId === productFamily.ID) {
            rangeProduct = currentNode;
            break;
          }
          if (!currentNode.ParentId) {
            break;
          }
          currentNode = allProducts.find((p) => p.ID === currentNode?.ParentId);
        }

        if (!rangeProduct) {
          return [];
        }

        // Get all series within the range (children of range)
        const seriesInRange = allProducts.filter(
          (product) => product?.ParentId === rangeProduct?.ID,
        );

        if (!seriesInRange.length) {
          return [];
        }

        // Get all products that have any of these series as parentId
        const seriesIds = seriesInRange
          .map((series) => series.ID)
          .filter((id): id is string => !!id);

        const products = allProducts.filter(
          (product) =>
            product?.ParentId && seriesIds.includes(product.ParentId),
        );

        if (!products.length) {
          return [];
        }

        // Get product IDs to find their subtypes (one level deeper)
        const productIds = products
          .map((product) => product.ID)
          .filter((id): id is string => !!id);

        // Get all product subtypes that have these products as parentId
        const productSubtypes = allProducts
          .filter(
            (subtype) =>
              subtype?.ParentId && productIds.includes(subtype.ParentId),
          )
          .sort((a, b) => Number(b.Sort) - Number(a.Sort));

        return productSubtypes?.length ? productSubtypes : [];
      }),
    );
  }

  /**
   * used to check the parent for displayRange, if true returns all range product ids else returns
   * the product id parameter as single item array
   */
  getProductDisplayRangesProductIDs(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ): Observable<string[]> {
    return this.getProductDataByCountryAndLanguageAndYear(
      {
        countryID,
        languageID,
        year,
      },
      context,
    ).pipe(
      map((response) => {
        const productSeries = response?.d?.results?.find(
          (productSerieses) => productSerieses.ParentId === productID,
        );
        const productRange = response?.d?.results?.find(
          (productRanges) =>
            productSeries?.ID && productSeries?.ParentId === productRanges?.ID,
        );
        const productFamily = response?.d?.results?.find(
          (productFamilies: DefaultProduct) =>
            productFamilies?.ID?.startsWith("pr_fam") &&
            productFamilies?.ID &&
            productRange?.ParentId &&
            productRange?.ParentId === productFamilies.ID,
        );

        // family is not supposed to display a range
        if (!productFamily?.DisplayRange) {
          return [productID];
        }

        // we have a range within product family with displayRanges true and
        // want all series
        const productIDs = response?.d?.results
          ?.filter((product) => {
            return productRange?.ID && product?.ParentId === productRange.ID;
          })
          .map((product) => product.ID);

        return productIDs?.length
          ? [...new Set([...(productIDs as string[])])]
          : [productID];
      }),
    );
  }
}
